import {useState} from 'react'
import {Modal, Button, Form} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import {useMutation} from '@apollo/client'
import {toast} from 'react-toastify'

import {CREATE_KANBAN_STAGE} from '../../../gql/mutations/kanban'
import {ICreateKanbanStageRes} from '../../../types/kanban'

interface Props {
  show: boolean
  workflowId: string
  onClose: () => void
  onCreated: () => void
}

const DEFAULT_COLOR = ''

export const ModalCreateKanbanStage: React.FC<Props> = ({show, workflowId, onClose, onCreated}) => {
  const intl = useIntl()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [isClosedState, setIsClosedState] = useState(false)
  const [color, setColor] = useState(DEFAULT_COLOR)

  const [createStage, {loading}] = useMutation<ICreateKanbanStageRes>(CREATE_KANBAN_STAGE)

  const handleClose = () => {
    if (loading) return
    setName('')
    setDescription('')
    setIsClosedState(false)
    setColor(DEFAULT_COLOR)
    onClose()
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!workflowId) {
      toast.error(intl.formatMessage({id: 'leads.kanban.selectFlowFirst', defaultMessage: 'Select a board before creating a column'}))
      return
    }

    try {
      const {data} = await createStage({
        variables: {
          workflowId,
          name,
          description: description || null,
          color: color || null,
          isClosedState,
        },
      })
      if (!data?.createKanbanStage?.ok) {
        toast.error(intl.formatMessage({id: 'leads.kanban.couldNotCreateStage', defaultMessage: 'The column could not be created.'}))
        return
      }
      toast.success(intl.formatMessage({id: 'leads.kanban.stageCreated', defaultMessage: 'Column created successfully'}))
      handleClose()
      onCreated()
    } catch (error) {
      console.error(error)
      toast.error(intl.formatMessage({id: 'leads.kanban.errCreatingStage', defaultMessage: 'Error creating the column'}))
    }
  }

  return (
    <Modal show={show} onHide={handleClose} centered className='kanban-stage-modal'>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton className='kanban-stage-modal__header'>
          <Modal.Title><FormattedMessage id='leads.kanban.newColumn' defaultMessage='New Column' /></Modal.Title>
        </Modal.Header>
        <Modal.Body className='kanban-stage-modal__body'>
          <Form.Group className='kanban-stage-modal__group'>
            <Form.Label><FormattedMessage id='common.field.name' defaultMessage='Name' /></Form.Label>
            <Form.Control
              className='kanban-stage-modal__input'
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={intl.formatMessage({id: 'leads.kanban.stageNamePlaceholder', defaultMessage: 'e.g. In progress'})}
              required
            />
          </Form.Group>
          <Form.Group className='kanban-stage-modal__group'>
            <Form.Label><FormattedMessage id='common.field.description' defaultMessage='Description' /></Form.Label>
            <Form.Control
              className='kanban-stage-modal__input'
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={intl.formatMessage({id: 'leads.kanban.optional', defaultMessage: 'Optional'})}
            />
          </Form.Group>
          <Form.Group className='kanban-stage-modal__group'>
            <Form.Label><FormattedMessage id='common.field.color' defaultMessage='Color' /></Form.Label>
            <div className='kanban-color-picker'>
              <Form.Control
                className='kanban-stage-modal__input kanban-stage-modal__input--color'
                type='color'
                value={color || '#ffffff'}
                onChange={(e) => setColor(e.target.value)}
                title={intl.formatMessage({id: 'leads.kanban.selectColorTooltip', defaultMessage: 'Select a color'})}
              />
              <Button variant='link' size='sm' onClick={() => setColor(DEFAULT_COLOR)}>
                <FormattedMessage id='leads.kanban.noColor' defaultMessage='No color' />
              </Button>
            </div>
          </Form.Group>
          <Form.Check
            className='kanban-stage-modal__checkbox'
            type='switch'
            id='kanban-stage-closed'
            label={intl.formatMessage({id: 'leads.kanban.markAsClosing', defaultMessage: 'Mark as closing stage'})}
            checked={isClosedState}
            onChange={(e) => setIsClosedState(e.target.checked)}
          />
        </Modal.Body>
        <Modal.Footer className='kanban-stage-modal__footer'>
          <Button variant='light' onClick={handleClose} disabled={loading}>
            <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
          </Button>
          <Button variant='primary' type='submit' disabled={loading}>
            {loading ? intl.formatMessage({id: 'leads.kanban.saving', defaultMessage: 'Saving...'}) : intl.formatMessage({id: 'common.action.save', defaultMessage: 'Save'})}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}
